import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowsClockwise, FloppyDisk, FilePdf, FileXls,
  ListChecks, Plus, ChartPieSlice, Buildings, X, Sparkle, Wrench,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import Header from "../components/Header";
import Footer from "../components/Footer";
import DishInfoSection from "../components/DishInfoSection";
import IngredientsTable from "../components/IngredientsTable";
import RecipeSteps from "../components/RecipeSteps";
import UtilitiesOverheads from "../components/UtilitiesOverheads";
import SummaryDashboard from "../components/SummaryDashboard";
import ScaleRecipe from "../components/ScaleRecipe";
import GuestFieldLock from "../components/GuestFieldLock";
import ManageCustomIngredientsDialog from "../components/dialogs/ManageCustomIngredientsDialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { api, computeSummary } from "../lib/kostra";
import { useAuth } from "../lib/auth";

const EMPTY_RECIPE = {
  dish_name: "",
  category: "Main Course",
  portion_size_g: 250,
  num_portions: 1,
  photo_path: null,
  notes: "",
  desired_food_cost_pct: 30,
  primary_ingredients: [],
  secondary_ingredients: [],
  utilities: { gas: 0, electricity: 0, water: 0, packaging: 0, delivery: 0 },
  overheads: { labour: 0, rent: 0, kitchen_overhead: 0, maintenance: 0, misc: 0 },
  steps: [],
};

const STORAGE_KEY = "kostra:draft";

export default function CalculatorPage() {
  const [recipe, setRecipe] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return { ...EMPTY_RECIPE, ...JSON.parse(raw) };
    } catch {}
    return EMPTY_RECIPE;
  });
  const [benchmarks, setBenchmarks] = useState([]);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [editingDishName, setEditingDishName] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("dish");
  const [searchParams, setSearchParams] = useSearchParams();
  const { guard, isAdmin } = useAuth();

  // load benchmarks
  const loadBenchmarks = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/ingredients/benchmarks");
      setBenchmarks(data.items || []);
      setRefreshedAt(data.refreshed_at);
    } catch (e) {
      toast.error("Failed to refresh prices");
    } finally {
      setRefreshing(false);
    }
  };
  useEffect(() => { loadBenchmarks(); }, []);

  // Load recipe for edit when ?edit=ID is present
  const editId = searchParams.get("edit");
  const fromChef = searchParams.get("from");
  useEffect(() => {
    if (!editId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/recipes/${editId}`);
        if (cancelled) return;
        const { summary: _s, ...rest } = data;
        const withIds = (arr) => (arr || []).map((it) => ({ ...it, id: it.id || crypto.randomUUID() }));
        const loaded = {
          ...EMPTY_RECIPE,
          ...rest,
          primary_ingredients: withIds(rest.primary_ingredients),
          secondary_ingredients: withIds(rest.secondary_ingredients),
        };
        setRecipe(loaded);
        setSavedId(data.id);
        setEditingDishName(data.dish_name || "Recipe");
        setTab("dish");
        toast.success(`Loaded "${data.dish_name}"`);
      } catch (e) {
        toast.error("Could not load recipe");
        setSearchParams({});
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  // Load a chef recipe template when ?from=chef:slug is present
  useEffect(() => {
    if (!fromChef || !fromChef.startsWith("chef:")) return;
    const slug = fromChef.slice(5);
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/chef-recipes/${slug}`);
        if (cancelled) return;
        const withIds = (arr) => (arr || []).map((it) => ({ ...it, id: crypto.randomUUID() }));
        const loaded = {
          ...EMPTY_RECIPE,
          dish_name: data.dish_name,
          category: data.category,
          portion_size_g: data.portion_size_g,
          num_portions: data.num_portions,
          notes: data.notes || "",
          desired_food_cost_pct: data.desired_food_cost_pct || 30,
          primary_ingredients: withIds(data.primary_ingredients),
          secondary_ingredients: withIds(data.secondary_ingredients),
          utilities: { ...EMPTY_RECIPE.utilities, ...(data.utilities || {}) },
          overheads: { ...EMPTY_RECIPE.overheads, ...(data.overheads || {}) },
          steps: data.steps || [],
        };
        setRecipe(loaded);
        setSavedId(null);
        setEditingDishName(null);
        setTab("dish");
        toast.success(`Loaded "${data.dish_name}" from Inspiration`);
        // strip the query param so refresh doesn't re-load
        setSearchParams({});
      } catch (e) {
        toast.error("Could not load chef recipe");
        setSearchParams({});
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromChef]);

  // autosave draft
  const saveTimer = useRef(null);
  useEffect(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(recipe)); } catch {}
    }, 350);
    return () => clearTimeout(saveTimer.current);
  }, [recipe]);

  const summary = useMemo(() => computeSummary(recipe), [recipe]);

  const persistRaw = async () => {
    if (!recipe.dish_name?.trim()) {
      toast.error("Add a dish name before saving");
      setTab("dish");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...recipe };
      if (savedId) {
        const { data } = await api.put(`/recipes/${savedId}`, payload);
        toast.success(`Saved ${data.dish_name}`);
      } else {
        const { data } = await api.post("/recipes", payload);
        setSavedId(data.id);
        toast.success(`Recipe saved`);
      }
    } catch (e) {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };
  const persist = guard(persistRaw, "save this recipe");

  const downloadFileRaw = async (kind) => {
    if (!recipe.dish_name?.trim()) {
      toast.error("Add a dish name first");
      setTab("dish");
      return;
    }
    try {
      const url = kind === "pdf" ? "/preview/pdf" : "/preview/excel";
      const { data } = await api.post(url, recipe, { responseType: "blob" });
      const blob = new Blob([data], {
        type: kind === "pdf"
          ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const ext = kind === "pdf" ? "pdf" : "xlsx";
      a.download = `KOSTRA_${(recipe.dish_name || "recipe").replace(/\s+/g, "_")}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(`${kind.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error("Export failed");
    }
  };
  const downloadFile = guard(downloadFileRaw, "download exports");

  const newRecipe = () => {
    setRecipe(EMPTY_RECIPE);
    setSavedId(null);
    setEditingDishName(null);
    setTab("dish");
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
    if (searchParams.get("edit")) setSearchParams({});
    toast.success("New recipe started");
  };

  return (
    <div className="app-shell">
      <Header />

      {/* Hero band */}
      <div className="bg-gradient-to-b from-[#0A3B24] to-[#0E5232] text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-8 pb-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="text-[#D4AF37] text-xs tracking-[0.25em] uppercase">Premium Cost Engine</div>
              <h1 className="font-display text-4xl md:text-5xl font-medium mt-2 leading-tight">
                Engineer your menu, <br className="hidden md:block" />
                <span className="text-[#D4AF37]">price with confidence.</span>
              </h1>
              <p className="text-white/75 text-sm md:text-base mt-3 max-w-2xl">
                Build accurate recipe costs, suggest market-competitive prices, and export
                a professional cost sheet for any cloud kitchen, bakery, or restaurant.
              </p>
            </div>
            <div className="flex flex-col items-stretch md:items-end gap-3 shrink-0">
              <div className="flex flex-wrap gap-2 md:justify-end" data-testid="hero-action-bar">
                <Button
                  onClick={persist}
                  disabled={saving}
                  className="bg-white/10 hover:bg-white/20 text-white border border-white/25 font-medium"
                  data-testid="save-recipe-btn"
                >
                  <FloppyDisk size={16} weight="bold" className="mr-2" />
                  {saving ? "Saving..." : (savedId ? "Update Recipe" : "Save Recipe")}
                </Button>
                <Button
                  onClick={() => downloadFile("pdf")}
                  className="bg-white text-[#0A3B24] hover:bg-[#FDF9EE] font-semibold border border-white"
                  data-testid="download-pdf-btn"
                >
                  <FilePdf size={16} weight="bold" className="mr-2" /> Download PDF
                </Button>
                <Button
                  onClick={() => downloadFile("excel")}
                  className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0A3B24] font-semibold"
                  data-testid="download-excel-btn"
                >
                  <FileXls size={16} weight="bold" className="mr-2" /> Download Excel
                </Button>
                <Link to="/inspiration" data-testid="hero-inspiration-link">
                  <Button
                    type="button"
                    className="bg-white text-[#0A3B24] hover:bg-[#FDF9EE] font-semibold border border-[#D4AF37]"
                  >
                    <Sparkle size={16} weight="fill" className="mr-2 text-[#D4AF37]" /> Recipe Library
                  </Button>
                </Link>
              </div>
              <div className="flex flex-wrap gap-2 md:justify-end">
                <Button
                  onClick={loadBenchmarks}
                  disabled={refreshing}
                  variant="outline"
                  className="bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white font-medium"
                  data-testid="refresh-prices-btn"
                >
                  <ArrowsClockwise size={16} weight="bold" className={`mr-2 ${refreshing ? "animate-spin" : ""}`} />
                  {refreshing ? "Refreshing..." : "Refresh Prices"}
                </Button>
                <Button
                  onClick={newRecipe}
                  variant="outline"
                  className="bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white font-medium"
                  data-testid="new-recipe-btn"
                >
                  <Plus size={16} weight="bold" className="mr-2" /> New Recipe
                </Button>
                <ManageCustomIngredientsDialog
                  trigger={
                    <Button
                      variant="outline"
                      className="bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white font-medium"
                      data-testid="manage-custom-ing-btn"
                    >
                      <Wrench size={16} weight="bold" className="mr-2" /> Custom Ingredients
                    </Button>
                  }
                  onChange={loadBenchmarks}
                />
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3 text-xs text-white/70">
            <span className="inline-flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-[#D4AF37]" />
              {benchmarks.length} curated ingredients
            </span>
            {refreshedAt && (
              <span>· prices refreshed {new Date(refreshedAt).toLocaleString()}</span>
            )}
            <span>· auto-saving draft</span>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-5 md:px-8 -mt-6 pb-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="space-y-6"
        >
          {!isAdmin && (
            <div
              className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-md bg-[#0A3B24]/5 border border-[#0A3B24]/15 text-sm"
              data-testid="guest-mode-banner"
            >
              <span className="inline-flex items-center gap-2 text-[#0A3B24]">
                <span className="h-2 w-2 rounded-full bg-[#D4AF37]" />
                <strong className="font-semibold">Read-only preview.</strong>
                <span className="text-[#374151]">Sign in to edit ingredients, save and export.</span>
              </span>
            </div>
          )}

          {editingDishName && (
            <div
              className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-md bg-[#FDF9EE] border border-[#D4AF37]/40 text-sm"
              data-testid="editing-banner"
            >
              <span className="inline-flex items-center gap-2 text-[#0A3B24]">
                <span className="h-2 w-2 rounded-full bg-[#D4AF37]" />
                Editing saved recipe — <strong className="font-semibold">{editingDishName}</strong>
              </span>
              <button
                type="button"
                onClick={newRecipe}
                className="inline-flex items-center gap-1 text-xs font-medium text-[#0A3B24] hover:text-[#0E5232] transition"
                data-testid="exit-edit-btn"
              >
                <X size={14} weight="bold" /> Exit edit mode
              </button>
            </div>
          )}

          <Tabs value={tab} onValueChange={setTab} className="space-y-6">
            <div className="kostra-card p-2 overflow-x-auto" data-testid="kostra-tabs">
              <TabsList className="bg-transparent p-0 gap-1.5 flex flex-nowrap md:flex-wrap w-full justify-start h-auto">
                <TabsTrigger
                  value="dish"
                  data-testid="tab-dish"
                  className="kostra-tab-trigger data-[state=active]:bg-[#0A3B24] data-[state=active]:text-white data-[state=active]:shadow-sm"
                >
                  <ListChecks size={18} weight="duotone" className="mr-2" />
                  <span>Dish &amp; Method</span>
                </TabsTrigger>
                <TabsTrigger
                  value="primary"
                  data-testid="tab-primary"
                  className="kostra-tab-trigger data-[state=active]:bg-[#0A3B24] data-[state=active]:text-white data-[state=active]:shadow-sm"
                >
                  <span className="mr-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#0A3B24]/10 text-[#0A3B24] text-[11px] font-semibold data-[state=active]:bg-white/20 data-[state=active]:text-white">P</span>
                  <span>Primary Ingredients</span>
                </TabsTrigger>
                <TabsTrigger
                  value="secondary"
                  data-testid="tab-secondary"
                  className="kostra-tab-trigger data-[state=active]:bg-[#0A3B24] data-[state=active]:text-white data-[state=active]:shadow-sm"
                >
                  <span className="mr-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#D4AF37]/15 text-[#0A3B24] text-[11px] font-semibold">S</span>
                  <span>Secondary Ingredients</span>
                </TabsTrigger>
                <TabsTrigger
                  value="utilities"
                  data-testid="tab-utilities"
                  className="kostra-tab-trigger data-[state=active]:bg-[#0A3B24] data-[state=active]:text-white data-[state=active]:shadow-sm"
                >
                  <Buildings size={18} weight="duotone" className="mr-2" />
                  <span>Utilities &amp; Overheads</span>
                </TabsTrigger>
                <TabsTrigger
                  value="summary"
                  data-testid="tab-summary"
                  className="kostra-tab-trigger data-[state=active]:bg-[#0A3B24] data-[state=active]:text-white data-[state=active]:shadow-sm"
                >
                  <ChartPieSlice size={18} weight="duotone" className="mr-2" />
                  <span>Summary</span>
                </TabsTrigger>
              </TabsList>
            </div>

            <GuestFieldLock>
            <TabsContent value="dish" className="space-y-6 m-0">
              <DishInfoSection recipe={recipe} onChange={setRecipe} />
              <ScaleRecipe recipe={recipe} onApply={setRecipe} />
              <RecipeSteps steps={recipe.steps} onChange={(steps) => setRecipe({ ...recipe, steps })} />
            </TabsContent>

            <TabsContent value="primary" className="m-0">
              <IngredientsTable
                title="Primary Raw Materials"
                subtitle="Proteins, dairy, grains, oils — your dish's foundation."
                testIdPrefix="primary"
                rows={recipe.primary_ingredients}
                onChange={(rows) => setRecipe({ ...recipe, primary_ingredients: rows })}
                benchmarks={benchmarks}
                benchmarkCategory="primary"
                total={summary.primary_total}
                accent="#0A3B24"
                onBenchmarksRefresh={loadBenchmarks}
              />
            </TabsContent>

            <TabsContent value="secondary" className="m-0">
              <IngredientsTable
                title="Secondary Raw Materials"
                subtitle="Spices, herbs, sauces, garnishes — the flavour layer."
                testIdPrefix="secondary"
                rows={recipe.secondary_ingredients}
                onChange={(rows) => setRecipe({ ...recipe, secondary_ingredients: rows })}
                benchmarks={benchmarks}
                benchmarkCategory="secondary"
                total={summary.secondary_total}
                accent="#D4AF37"
                onBenchmarksRefresh={loadBenchmarks}
              />
            </TabsContent>

            <TabsContent value="utilities" className="m-0">
              <UtilitiesOverheads
                utilities={recipe.utilities}
                overheads={recipe.overheads}
                onUtilities={(u) => setRecipe({ ...recipe, utilities: u })}
                onOverheads={(o) => setRecipe({ ...recipe, overheads: o })}
              />
            </TabsContent>

            <TabsContent value="summary" className="m-0">
              <SummaryDashboard
                summary={summary}
                recipe={recipe}
                onChangeFoodCostPct={(v) => setRecipe({ ...recipe, desired_food_cost_pct: v })}
              />
            </TabsContent>
            </GuestFieldLock>
          </Tabs>
        </motion.div>
      </main>

      <Footer />
    </div>
  );
}
